import { z } from "zod/v4";

export const RENDER_CHART_TOOL_NAME = "renderChart" as const;
export const PRISM_CHART_SCHEMA_VERSION = "prism-chart-v1" as const;

export const E_PrismChartStyleStatus = {
  ACTIVE: "active",
  LEGACY: "legacy",
  DEPRECATED: "deprecated",
  PLANNED: "planned",
  UNSUPPORTED: "unsupported",
} as const;

export type T_PrismChartStyleStatus =
  (typeof E_PrismChartStyleStatus)[keyof typeof E_PrismChartStyleStatus];

export const z_ChartType = z.enum(["bar", "line", "pie", "area"]);
export type T_ChartType = z.infer<typeof z_ChartType>;

export const z_ChartValueFormat = z.enum([
  "number",
  "currency",
  "percent",
  "ratio",
  "multiple",
  "bps",
]);
export type T_ChartValueFormat = z.infer<typeof z_ChartValueFormat>;

export const CHART_PRESENTATION_KINDS = [
  "expected_actual",
  "dual_axis_bar_line",
] as const;

export const z_ChartPresentationKind = z.enum(CHART_PRESENTATION_KINDS);
export type T_ChartPresentationKind = z.infer<typeof z_ChartPresentationKind>;

export const z_PrismChartActiveStyle = z.enum([
  "line",
  "area",
  "bar",
  "grouped_bar",
  "stacked_bar",
  "horizontal_bar",
  "pie",
  "donut",
]);
export type T_PrismChartActiveStyle = z.infer<typeof z_PrismChartActiveStyle>;

export const z_PrismChartHistoricalStyle = z.enum([
  "line",
  "area",
  "bar",
  "grouped_bar",
  "stacked_bar",
  "horizontal_bar",
  "pie",
  "donut",
  "radar",
  "candlestick",
  "ohlc",
  "waterfall",
  "scatter",
  "bubble",
  "histogram",
  "box_plot",
  "heatmap",
  "combo",
  "dual_axis",
  "treemap",
  "sankey",
]);
export type T_PrismChartHistoricalStyle = z.infer<
  typeof z_PrismChartHistoricalStyle
>;

const z_NumericValue = z.union([
  z.number(),
  z
    .string()
    .regex(/^-?\d+(\.\d+)?$/, "Value must be a number")
    .transform(Number),
]);

const z_SingleSeriesDataPoint = z.object({
  label: z.string().min(1, "Label is required"),
  value: z_NumericValue,
  // Optional long-form point label (e.g. "Jul 24, 10:20 AM") for scrub/header
  // readouts. Axis rendering keeps using the short `label`.
  detailLabel: z.string().min(1).optional(),
});

const z_MultiSeriesDataPoint = z
  .record(z.string(), z.union([z.string(), z.number(), z.null()]))
  .superRefine((point, ctx) => {
    const label = point["label"];
    if (typeof label !== "string" || label.trim().length === 0) {
      ctx.addIssue({
        code: "custom",
        path: ["label"],
        message: "Each data point must have a label",
      });
    }

    for (const [key, value] of Object.entries(point)) {
      if (
        key === "label" ||
        key === "detailLabel" ||
        value === null ||
        typeof value === "number"
      ) {
        continue;
      }
      if (typeof value === "string" && /^-?\d+(\.\d+)?$/.test(value)) {
        continue;
      }
      ctx.addIssue({
        code: "custom",
        path: [key],
        message: "Series values must be numbers or null",
      });
    }
  })
  .transform((point) => {
    const normalized: Record<string, string | number | null> = {};

    for (const [key, value] of Object.entries(point)) {
      if (key === "label" || key === "detailLabel") {
        normalized[key] = typeof value === "string" ? value : String(value);
        continue;
      }

      if (typeof value === "string") {
        normalized[key] = Number(value);
        continue;
      }

      normalized[key] = value;
    }

    return normalized;
  });

export const z_ChartDataPoint = z.union([
  z_SingleSeriesDataPoint,
  z_MultiSeriesDataPoint,
]);
export type T_ChartDataPoint = z.infer<typeof z_ChartDataPoint>;
export type T_SingleSeriesDataPoint = z.infer<typeof z_SingleSeriesDataPoint>;
export type T_MultiSeriesDataPoint = z.infer<typeof z_MultiSeriesDataPoint>;

export const MAX_RENDER_CHART_DATA_POINTS = 2_500;

const z_ChartPresentationView = z.object({
  key: z.string().min(1).max(40),
  label: z.string().min(1).max(40),
  data: z
    .array(z_ChartDataPoint)
    .min(2, "Chart view must have at least 2 data points")
    .max(
      MAX_RENDER_CHART_DATA_POINTS,
      `Chart view cannot have more than ${MAX_RENDER_CHART_DATA_POINTS} data points`,
    ),
});
export type T_ChartPresentationView = z.infer<typeof z_ChartPresentationView>;

export const z_ExpectedActualChartPresentation = z.object({
  kind: z.literal("expected_actual"),
  expectedSeries: z.string().min(1),
  actualSeries: z.string().min(1),
  aboveExpectationLabel: z.string().min(1).max(40).optional(),
  belowExpectationLabel: z.string().min(1).max(40).optional(),
  pendingExpectedPoints: z
    .array(
      z.object({
        label: z.string().min(1).max(80),
        value: z.number().finite(),
      }),
    )
    .max(12)
    .optional(),
});
export type T_ExpectedActualChartPresentation = z.infer<
  typeof z_ExpectedActualChartPresentation
>;

export const z_DualAxisBarLineChartPresentation = z.object({
  kind: z.literal("dual_axis_bar_line"),
  barSeries: z.tuple([z.string().min(1), z.string().min(1)]),
  lineSeries: z.string().min(1),
  rightValueFormat: z_ChartValueFormat.optional(),
  rightValuePrefix: z.string().min(1).max(12).optional(),
  rightValueSuffix: z.string().min(1).max(12).optional(),
  defaultViewKey: z.string().min(1).max(40).optional(),
  views: z.array(z_ChartPresentationView).min(2).max(5).optional(),
});
export type T_DualAxisBarLineChartPresentation = z.infer<
  typeof z_DualAxisBarLineChartPresentation
>;

export const z_ChartPresentation = z.discriminatedUnion("kind", [
  z_ExpectedActualChartPresentation,
  z_DualAxisBarLineChartPresentation,
]);
export type T_ChartPresentation = z.infer<typeof z_ChartPresentation>;

function getChartPointValue(
  point: T_ChartDataPoint,
  seriesName: string,
): number | null | undefined {
  const entry = Object.entries(point).find(([key]) => key === seriesName);
  if (!entry) return undefined;
  return typeof entry[1] === "number" || entry[1] === null
    ? entry[1]
    : undefined;
}

/**
 * Renderer model consumed by mobile/web chart components. Specialized visual
 * grammar is additive presentation metadata over the same numeric series so
 * older clients can safely fall back to an ordinary line or bar chart.
 */
export const z_ChartData = z
  .object({
    type: z_ChartType,
    title: z.string().optional(),
    series: z.array(z.string().min(1)).max(10).optional(),
    valueFormat: z_ChartValueFormat.optional(),
    valuePrefix: z.string().min(1).max(12).optional(),
    valueSuffix: z.string().min(1).max(12).optional(),
    presentation: z_ChartPresentation.optional(),
    data: z
      .array(z_ChartDataPoint)
      .min(2, "Chart must have at least 2 data points")
      .max(
        MAX_RENDER_CHART_DATA_POINTS,
        `Chart cannot have more than ${MAX_RENDER_CHART_DATA_POINTS} data points`,
      ),
  })
  .superRefine((chart, ctx) => {
    const presentation = chart.presentation;
    if (!presentation) return;

    const requiredSeries =
      presentation.kind === "expected_actual"
        ? [presentation.expectedSeries, presentation.actualSeries]
        : [...presentation.barSeries, presentation.lineSeries];
    const uniqueRequiredSeries = new Set(requiredSeries);
    if (uniqueRequiredSeries.size !== requiredSeries.length) {
      ctx.addIssue({
        code: "custom",
        message: "Chart presentation series must be distinct",
        path: ["presentation"],
      });
    }

    for (const seriesName of requiredSeries) {
      if (!chart.series?.includes(seriesName)) {
        ctx.addIssue({
          code: "custom",
          message: `Presentation series ${seriesName} must be declared in chart.series`,
          path: ["series"],
        });
      }
    }

    const views =
      presentation.kind === "dual_axis_bar_line" && presentation.views
        ? presentation.views
        : [];
    const dataSets = [
      { data: chart.data, path: ["data"] as const },
      ...views.map((view, index) => ({
        data: view.data,
        path: ["presentation", "views", index, "data"] as const,
      })),
    ];

    for (const dataSet of dataSets) {
      dataSet.data.forEach((point, pointIndex) => {
        for (const seriesName of requiredSeries) {
          const value = getChartPointValue(point, seriesName);
          const actualMayBeNull =
            presentation.kind === "expected_actual" &&
            seriesName === presentation.actualSeries;
          if (value === undefined || (value === null && !actualMayBeNull)) {
            ctx.addIssue({
              code: "custom",
              message: `${seriesName} must be numeric${actualMayBeNull ? " or null" : ""}`,
              path: [...dataSet.path, pointIndex, seriesName],
            });
          }
        }
      });
    }

    if (presentation.kind !== "dual_axis_bar_line" || !presentation.views) {
      return;
    }

    const viewKeys = presentation.views.map((view) => view.key);
    if (new Set(viewKeys).size !== viewKeys.length) {
      ctx.addIssue({
        code: "custom",
        message: "Chart presentation view keys must be unique",
        path: ["presentation", "views"],
      });
    }
    if (
      !presentation.defaultViewKey ||
      !viewKeys.includes(presentation.defaultViewKey)
    ) {
      ctx.addIssue({
        code: "custom",
        message: "defaultViewKey must identify one of the chart views",
        path: ["presentation", "defaultViewKey"],
      });
      return;
    }

    const defaultView = presentation.views.find(
      (view) => view.key === presentation.defaultViewKey,
    );
    if (
      defaultView &&
      JSON.stringify(defaultView.data) !== JSON.stringify(chart.data)
    ) {
      ctx.addIssue({
        code: "custom",
        message: "The default chart view must match chart.data",
        path: ["presentation", "views"],
      });
    }
  });
export type T_ChartData = z.infer<typeof z_ChartData>;

const z_PrismChartAxisValue = z.union([z.string(), z.number()]);

function buildPrismChartAxisSchema(maxDataPoints: number) {
  return z.object({
    data: z.array(z_PrismChartAxisValue).min(2).max(maxDataPoints).optional(),
    title: z.string().min(1).max(100).optional(),
    type: z.enum(["category", "time", "number"]).optional(),
    min: z_NumericValue.nullish(),
    max: z_NumericValue.nullish(),
    format: z_ChartValueFormat.optional(),
  });
}

function buildPrismChartSeriesSchema(maxDataPoints: number) {
  return z.object({
    name: z.string().min(1).max(100),
    values: z
      .array(z.union([z_NumericValue, z.null()]))
      .min(2)
      .max(maxDataPoints),
    color: z.string().min(1).max(32).optional(),
    unit: z.string().min(1).max(40).optional(),
  });
}

const z_PrismChartAxis = buildPrismChartAxisSchema(50);
const z_PrismChartHistoricalAxis = buildPrismChartAxisSchema(
  MAX_RENDER_CHART_DATA_POINTS,
);
type T_PrismChartAxis = z.infer<typeof z_PrismChartAxis>;

const z_PrismChartSeries = buildPrismChartSeriesSchema(50);
const z_PrismChartHistoricalSeries = buildPrismChartSeriesSchema(
  MAX_RENDER_CHART_DATA_POINTS,
);

const z_PrismChartSource = z.object({
  label: z.string().min(1).max(120).optional(),
  ticker: z.string().min(1).max(20).optional(),
  metric: z.string().min(1).max(80).optional(),
  asOf: z.string().min(1).max(80).optional(),
  freshness: z.string().min(1).max(80).optional(),
});

// Historical persisted rows predate today's generation limits. Source metadata
// is not rendered, so oversized legacy descriptions must not invalidate an
// otherwise valid chart during read-time normalization or migration.
const z_PrismChartHistoricalSource = z.object({
  label: z.string().min(1).optional(),
  ticker: z.string().min(1).optional(),
  metric: z.string().min(1).optional(),
  asOf: z.string().min(1).optional(),
  freshness: z.string().min(1).optional(),
});

const z_PrismChartDisplayHints = z.object({
  stacked: z.boolean().optional(),
  horizontal: z.boolean().optional(),
  showDataTable: z.boolean().optional(),
});

function buildPrismChartV1Schema(
  styleSchema: z.ZodType,
  axisSchema = z_PrismChartAxis,
  seriesSchema = z_PrismChartSeries,
  sourceSchema = z_PrismChartSource,
) {
  return z
    .object({
      version: z.literal(PRISM_CHART_SCHEMA_VERSION),
      style: styleSchema,
      title: z.string().min(1).max(140).optional(),
      subtitle: z.string().min(1).max(200).optional(),
      series: z.array(seriesSchema).min(1).max(10),
      x_axis: axisSchema.optional(),
      y_axis: z.union([axisSchema, z.array(axisSchema)]).optional(),
      source: sourceSchema.optional(),
      display: z_PrismChartDisplayHints.optional(),
      warnings: z.array(z.string().min(1).max(200)).max(5).optional(),
    })
    .superRefine((chart, ctx) => {
      const xAxisLength = chart.x_axis?.data?.length;
      if (xAxisLength === undefined) return;

      for (const [index, series] of chart.series.entries()) {
        if (series.values.length !== xAxisLength) {
          ctx.addIssue({
            code: "custom",
            path: ["series", index, "values"],
            message: "Series value count must match x_axis.data count",
          });
        }
      }
    });
}

export const z_PrismChartV1Generatable = buildPrismChartV1Schema(
  z_PrismChartActiveStyle,
);
export type T_PrismChartV1Generatable = z.infer<
  typeof z_PrismChartV1Generatable
>;

export const z_PrismChartV1Historical = buildPrismChartV1Schema(
  z_PrismChartHistoricalStyle,
  z_PrismChartHistoricalAxis,
  z_PrismChartHistoricalSeries,
  z_PrismChartHistoricalSource,
);
export type T_PrismChartV1Historical = z.infer<typeof z_PrismChartV1Historical>;

const z_PrismChartLegacyDataBridge = z.object({
  version: z.literal(PRISM_CHART_SCHEMA_VERSION).optional(),
  style: z_PrismChartActiveStyle,
  title: z.string().min(1).max(140).optional(),
  subtitle: z.string().min(1).max(200).optional(),
  series: z.array(z.string().min(1)).max(10).optional(),
  data: z
    .array(z_ChartDataPoint)
    .min(2, "Chart must have at least 2 data points")
    .max(
      MAX_RENDER_CHART_DATA_POINTS,
      `Chart cannot have more than ${MAX_RENDER_CHART_DATA_POINTS} data points`,
    ),
  source: z_PrismChartSource.optional(),
  display: z_PrismChartDisplayHints.optional(),
  warnings: z.array(z.string().min(1).max(200)).max(5).optional(),
});

const z_RenderChartInputShape = z
  .object({
    version: z.literal(PRISM_CHART_SCHEMA_VERSION).optional(),
    style: z_PrismChartActiveStyle.optional(),
    type: z_ChartType.optional(),
    title: z.string().min(1).max(140).optional(),
    subtitle: z.string().min(1).max(200).optional(),
    series: z
      .union([z.array(z.string().min(1)).max(10), z.array(z_PrismChartSeries)])
      .optional(),
    data: z
      .array(z_ChartDataPoint)
      .min(2)
      .max(MAX_RENDER_CHART_DATA_POINTS)
      .optional(),
    x_axis: z_PrismChartAxis.optional(),
    y_axis: z.union([z_PrismChartAxis, z.array(z_PrismChartAxis)]).optional(),
    source: z_PrismChartSource.optional(),
    display: z_PrismChartDisplayHints.optional(),
    warnings: z.array(z.string().min(1).max(200)).max(5).optional(),
  })
  .superRefine((input, ctx) => {
    if (z_ChartData.safeParse(input).success) return;
    if (z_PrismChartV1Generatable.safeParse(input).success) return;
    if (z_PrismChartLegacyDataBridge.safeParse(input).success) return;

    ctx.addIssue({
      code: "custom",
      message:
        "renderChart input must be either legacy {type,data} chart data or prism-chart-v1 with an active chart style",
    });
  });

export const z_RenderChartInput = z_RenderChartInputShape.describe(
  "Chart specification. Prefer prism-chart-v1 {version, style, series, x_axis, y_axis}; legacy {type, data, series} is accepted for compatibility.",
);

export type T_RenderChartInput = z.infer<typeof z_RenderChartInput>;

export type T_ChartNormalizationResult =
  | {
      kind: "chart";
      chart: T_ChartData;
      originalStyle: string;
      status: T_PrismChartStyleStatus;
      warnings: string[];
    }
  | {
      kind: "unsupported";
      originalStyle: string;
      status: T_PrismChartStyleStatus;
      title: string | null;
      reason: string;
      warnings: string[];
    };

function parseSerializedJsonArg(raw: string | null | undefined): unknown {
  if (raw == null) return undefined;

  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
}

function normalizeStringValue(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function normalizeOptionalObjectArg(
  args: Record<string, string | null | undefined>,
  key: string,
): unknown {
  const raw = args[key];
  const parsed = parseSerializedJsonArg(raw);
  return parsed === undefined ? raw : parsed;
}

function rehydrateRenderChartToolArgs(
  args: Record<string, string | null | undefined>,
): Record<string, unknown> {
  return {
    version: normalizeStringValue(args["version"]),
    style: normalizeStringValue(args["style"]),
    type: normalizeStringValue(args["type"]),
    title: normalizeStringValue(args["title"]),
    subtitle: normalizeStringValue(args["subtitle"]),
    series: normalizeOptionalObjectArg(args, "series"),
    data: normalizeOptionalObjectArg(args, "data"),
    x_axis: normalizeOptionalObjectArg(args, "x_axis"),
    y_axis: normalizeOptionalObjectArg(args, "y_axis"),
    source: normalizeOptionalObjectArg(args, "source"),
    display: normalizeOptionalObjectArg(args, "display"),
    warnings: normalizeOptionalObjectArg(args, "warnings"),
  };
}

export function getPrismChartStyleStatus(
  style: string,
): T_PrismChartStyleStatus {
  switch (style) {
    case "line":
    case "area":
    case "bar":
    case "grouped_bar":
    case "stacked_bar":
    case "horizontal_bar":
    case "pie":
    case "donut":
      return E_PrismChartStyleStatus.ACTIVE;
    case "radar":
      return E_PrismChartStyleStatus.DEPRECATED;
    case "candlestick":
    case "ohlc":
    case "waterfall":
    case "scatter":
    case "bubble":
    case "histogram":
    case "box_plot":
    case "heatmap":
    case "combo":
    case "dual_axis":
    case "treemap":
      return E_PrismChartStyleStatus.PLANNED;
    case "sankey":
    default:
      return E_PrismChartStyleStatus.UNSUPPORTED;
  }
}

function getRenderableChartTypeForStyle(style: string): T_ChartType | null {
  switch (style) {
    case "line":
      return "line";
    case "area":
      return "area";
    case "pie":
    case "donut":
      return "pie";
    case "bar":
    case "grouped_bar":
    case "stacked_bar":
    case "horizontal_bar":
    case "radar":
      return "bar";
    default:
      return null;
  }
}

function isStringSeriesList(series: unknown): series is readonly string[] {
  return (
    Array.isArray(series) && series.every((item) => typeof item === "string")
  );
}

function inferSeriesNamesFromRows(
  data: T_ChartDataPoint[],
): string[] | undefined {
  const seriesNames: string[] = [];
  const seen = new Set<string>();

  for (const point of data) {
    for (const key of Object.keys(point)) {
      if (
        key === "label" ||
        key === "value" ||
        key === "detailLabel" ||
        seen.has(key)
      ) {
        continue;
      }
      seen.add(key);
      seriesNames.push(key);
    }
  }

  return seriesNames.length > 0 ? seriesNames : undefined;
}

function normalizeRendererChartData(chart: T_ChartData): T_ChartData {
  const baseChart = {
    type: chart.type,
    ...(chart.title === undefined ? {} : { title: chart.title }),
    ...(chart.valueFormat === undefined
      ? {}
      : { valueFormat: chart.valueFormat }),
    ...(chart.valuePrefix === undefined
      ? {}
      : { valuePrefix: chart.valuePrefix }),
    ...(chart.valueSuffix === undefined
      ? {}
      : { valueSuffix: chart.valueSuffix }),
    ...(chart.presentation === undefined
      ? {}
      : { presentation: chart.presentation }),
  };

  if (chart.type === "pie") {
    return {
      ...baseChart,
      data: chart.data,
    };
  }

  const series = chart.series ?? inferSeriesNamesFromRows(chart.data);
  if (series === undefined) {
    return {
      ...baseChart,
      data: chart.data,
    };
  }

  return {
    ...baseChart,
    series,
    data: chart.data,
  };
}

function getPrimaryYAxis(
  chart: T_PrismChartV1Historical,
): T_PrismChartAxis | undefined {
  if (Array.isArray(chart.y_axis)) {
    return chart.y_axis[0];
  }

  return chart.y_axis;
}

function getFirstSeriesUnit(
  chart: T_PrismChartV1Historical,
): string | undefined {
  for (const series of chart.series) {
    const unit = series.unit?.trim();
    if (unit && unit.length > 0) return unit;
  }

  return undefined;
}

function inferValueFormatFromUnit(
  unit: string | undefined,
): T_ChartValueFormat | undefined {
  if (unit === undefined) return undefined;

  switch (unit.trim().toLowerCase()) {
    case "%":
    case "percent":
    case "percentage":
      return "percent";
    case "$":
    case "usd":
      return "currency";
    case "bps":
    case "bp":
      return "bps";
    case "x":
      return "multiple";
    default:
      return undefined;
  }
}

function getDefaultSuffixForValueFormat(
  valueFormat: T_ChartValueFormat | undefined,
): string | undefined {
  switch (valueFormat) {
    case "percent":
      return "%";
    case "bps":
      return " bps";
    case "multiple":
      return "x";
    default:
      return undefined;
  }
}

function getDefaultPrefixForValueFormat(
  valueFormat: T_ChartValueFormat | undefined,
): string | undefined {
  return valueFormat === "currency" ? "$" : undefined;
}

function buildRendererValueMetadata(
  chart: T_PrismChartV1Historical,
): Pick<T_ChartData, "valueFormat" | "valuePrefix" | "valueSuffix"> {
  const yAxis = getPrimaryYAxis(chart);
  const unit = getFirstSeriesUnit(chart);
  const valueFormat = yAxis?.format ?? inferValueFormatFromUnit(unit);
  const unitIsCurrencyPrefix = unit === "$";
  const valuePrefix = unitIsCurrencyPrefix
    ? unit
    : getDefaultPrefixForValueFormat(valueFormat);
  const valueSuffix =
    unit !== undefined && !unitIsCurrencyPrefix
      ? unit
      : getDefaultSuffixForValueFormat(valueFormat);

  return {
    ...(valueFormat === undefined ? {} : { valueFormat }),
    ...(valuePrefix === undefined ? {} : { valuePrefix }),
    ...(valueSuffix === undefined ? {} : { valueSuffix }),
  };
}

function normalizeLegacyChartData(
  input: unknown,
): T_ChartNormalizationResult | null {
  const parsed = z_ChartData.safeParse(input);
  if (!parsed.success) return null;

  return {
    kind: "chart",
    chart: normalizeRendererChartData(parsed.data),
    originalStyle: parsed.data.type,
    status: E_PrismChartStyleStatus.LEGACY,
    warnings: [],
  };
}

function normalizePrismChartLegacyDataBridge(
  input: unknown,
): T_ChartNormalizationResult | null {
  const parsed = z_PrismChartLegacyDataBridge.safeParse(input);
  if (!parsed.success) return null;

  const chart = parsed.data;
  const style = chart.style;
  const status = getPrismChartStyleStatus(style);
  const renderType = getRenderableChartTypeForStyle(style);
  if (renderType === null) {
    return {
      kind: "unsupported",
      originalStyle: style,
      status,
      title: chart.title ?? null,
      reason: "This chart style is not supported.",
      warnings: chart.warnings ?? [],
    };
  }

  const chartData = z_ChartData.safeParse({
    type: renderType,
    title: chart.title,
    ...(chart.series !== undefined && renderType !== "pie"
      ? { series: chart.series }
      : {}),
    data: chart.data,
  });

  if (!chartData.success) {
    return {
      kind: "unsupported",
      originalStyle: style,
      status,
      title: chart.title ?? null,
      reason: chartData.error.issues[0]?.message ?? "Chart data is invalid.",
      warnings: chart.warnings ?? [],
    };
  }

  return {
    kind: "chart",
    chart: normalizeRendererChartData(chartData.data),
    originalStyle: style,
    status,
    warnings: chart.warnings ?? [],
  };
}

function buildLabels(chart: T_PrismChartV1Historical): string[] {
  const axisData = chart.x_axis?.data;
  if (axisData && axisData.length > 0) {
    return axisData.map((value) => String(value));
  }

  const firstSeries = chart.series[0];
  if (!firstSeries) return [];

  return firstSeries.values.map((_, index) => String(index + 1));
}

function buildSingleSeriesRows(args: {
  labels: string[];
  values: (number | null)[];
}): T_SingleSeriesDataPoint[] {
  const rows: T_SingleSeriesDataPoint[] = [];

  for (const [index, value] of args.values.entries()) {
    const label = args.labels[index];
    if (label === undefined || value === null) continue;
    rows.push({ label, value });
  }

  return rows;
}

function buildMultiSeriesRows(args: {
  labels: string[];
  series: T_PrismChartV1Historical["series"];
}): T_MultiSeriesDataPoint[] {
  const rows: T_MultiSeriesDataPoint[] = [];

  for (const [index, label] of args.labels.entries()) {
    const row: Record<string, string | number | null> = { label };

    for (const series of args.series) {
      row[series.name] = series.values[index] ?? null;
    }

    rows.push(row);
  }

  return rows;
}

function normalizePrismChartV1(
  chart: T_PrismChartV1Historical,
): T_ChartNormalizationResult {
  const style = String(chart.style);
  const status = getPrismChartStyleStatus(style);
  const renderType = getRenderableChartTypeForStyle(style);
  if (renderType === null) {
    return {
      kind: "unsupported",
      originalStyle: style,
      status,
      title: chart.title ?? null,
      reason:
        status === E_PrismChartStyleStatus.PLANNED
          ? "This chart style is recognized but not renderable in chat yet."
          : "This chart style is not supported.",
      warnings: chart.warnings ?? [],
    };
  }

  const labels = buildLabels(chart);
  const seriesNames = chart.series.map((series) => series.name);
  const firstSeries = chart.series[0];
  if (!firstSeries) {
    return {
      kind: "unsupported",
      originalStyle: style,
      status,
      title: chart.title ?? null,
      reason: "Chart has no series to render.",
      warnings: chart.warnings ?? [],
    };
  }

  const data =
    chart.series.length === 1 || renderType === "pie"
      ? buildSingleSeriesRows({
          labels,
          values: firstSeries.values,
        })
      : buildMultiSeriesRows({
          labels,
          series: chart.series,
        });

  const chartData = z_ChartData.safeParse({
    type: renderType,
    title: chart.title,
    ...buildRendererValueMetadata(chart),
    ...(chart.series.length > 1 && renderType !== "pie"
      ? { series: seriesNames }
      : {}),
    data,
  });

  if (!chartData.success) {
    return {
      kind: "unsupported",
      originalStyle: style,
      status,
      title: chart.title ?? null,
      reason: chartData.error.issues[0]?.message ?? "Chart data is invalid.",
      warnings: chart.warnings ?? [],
    };
  }

  const deprecationWarning =
    status === E_PrismChartStyleStatus.DEPRECATED
      ? [`${style} charts are deprecated; rendered as ${renderType}.`]
      : [];

  return {
    kind: "chart",
    chart: normalizeRendererChartData(chartData.data),
    originalStyle: style,
    status,
    warnings: [...deprecationWarning, ...(chart.warnings ?? [])],
  };
}

export function normalizeChartInputForRendering(
  input: unknown,
): T_ChartNormalizationResult {
  const legacy = normalizeLegacyChartData(input);
  if (legacy !== null) return legacy;

  const bridged = normalizePrismChartLegacyDataBridge(input);
  if (bridged !== null) return bridged;

  const prismChart = z_PrismChartV1Historical.safeParse(input);
  if (prismChart.success) {
    return normalizePrismChartV1(prismChart.data);
  }

  return {
    kind: "unsupported",
    originalStyle: "unknown",
    status: E_PrismChartStyleStatus.UNSUPPORTED,
    title: null,
    reason: "Chart payload does not match a supported historical schema.",
    warnings: [],
  };
}

function parseChartJson(raw: string): T_ChartNormalizationResult | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    return normalizeChartInputForRendering(parsed);
  } catch {
    return null;
  }
}

function extractChartJsonFromText(
  text: string,
): T_ChartNormalizationResult | null {
  const trimmed = text.trim();
  if (trimmed.length === 0) return null;

  const directChartJson = parseChartJson(trimmed);
  if (directChartJson !== null) return directChartJson;

  const firstBraceIndex = trimmed.indexOf("{");
  const lastBraceIndex = trimmed.lastIndexOf("}");
  if (firstBraceIndex === -1 || lastBraceIndex <= firstBraceIndex) return null;

  return parseChartJson(trimmed.slice(firstBraceIndex, lastBraceIndex + 1));
}

export function renderChartToolArgsToChartResult(
  args: Record<string, string | null | undefined>,
): T_ChartNormalizationResult {
  return normalizeChartInputForRendering(rehydrateRenderChartToolArgs(args));
}

export function renderChartToolArgsToChartJson(
  args: Record<string, string | null | undefined>,
): string | null {
  const result = renderChartToolArgsToChartResult(args);
  if (result.kind !== "chart") return null;

  return JSON.stringify(result.chart);
}

export function renderChartToolPayloadToChartResult(args: {
  toolArgs: Record<string, string | null | undefined>;
  toolResponse: string;
}): T_ChartNormalizationResult {
  const argsResult = renderChartToolArgsToChartResult(args.toolArgs);
  if (argsResult.kind === "chart") return argsResult;

  const responseResult = extractChartJsonFromText(args.toolResponse);
  return responseResult ?? argsResult;
}

export function renderChartToolPayloadToChartJson(args: {
  toolArgs: Record<string, string | null | undefined>;
  toolResponse: string;
}): string | null {
  const result = renderChartToolPayloadToChartResult(args);
  if (result.kind !== "chart") return null;

  return JSON.stringify(result.chart);
}

export function isMultiSeries(data: T_ChartData): boolean {
  return Array.isArray(data.series) && data.series.length > 0;
}

export function getSeriesNames(data: T_ChartData): string[] {
  if (isMultiSeries(data) && data.series) {
    return data.series;
  }
  return ["value"];
}

function isSingleSeriesPoint(
  point: T_ChartDataPoint,
): point is T_SingleSeriesDataPoint {
  return "value" in point && typeof point.value === "number";
}

export function getSeriesValue(
  point: T_ChartDataPoint,
  seriesName: string,
): number | null {
  if (seriesName === "value" && "value" in point) {
    return typeof point.value === "number" ? point.value : null;
  }
  if (isSingleSeriesPoint(point)) {
    return null;
  }
  const value = point[seriesName];
  return typeof value === "number" ? value : null;
}

export const E_BarSortMode = {
  DESC: "desc",
  ASC: "asc",
  ORIGINAL: "original",
} as const;

export type T_BarSortMode = (typeof E_BarSortMode)[keyof typeof E_BarSortMode];

export function sortBarChartData(
  data: T_ChartData,
  mode: T_BarSortMode = E_BarSortMode.DESC,
): T_ChartData {
  if (mode === E_BarSortMode.ORIGINAL) {
    return data;
  }

  const seriesNames = getSeriesNames(data);
  const direction = mode === E_BarSortMode.ASC ? 1 : -1;

  const sorted = [...data.data].sort((a, b) => {
    const sumA = seriesNames.reduce(
      (sum, name) => sum + (getSeriesValue(a, name) ?? 0),
      0,
    );
    const sumB = seriesNames.reduce(
      (sum, name) => sum + (getSeriesValue(b, name) ?? 0),
      0,
    );
    return (sumA - sumB) * direction;
  });

  return { ...data, data: sorted };
}

export function getChartSeriesForPrompt(): string {
  return [
    "Prefer prism-chart-v1.",
    "Active styles: line, area, bar, grouped_bar, stacked_bar, horizontal_bar, pie, donut.",
    "Deprecated styles are accepted only for historical chats and must not be generated.",
    "Planned styles such as candlestick, scatter, heatmap, and waterfall are not generatable until their renderers ship.",
  ].join(" ");
}

export function isStringSeriesArray(value: unknown): boolean {
  return isStringSeriesList(value);
}
